import { INDUSTRY_OPTIONS } from "@/components/ui/selects/options/contact";
import type { QuickFilter } from "@/components/shared/DataTable-modular/types";
import { COMPANY_STATUS_VALUES } from "@/lib/constants/company-statuses";

const COMPANY_STATUS_LABELS: Record<(typeof COMPANY_STATUS_VALUES)[number], string> = {
  active: "Active",
  inactive: "Inactive",
  prospect: "Prospect",
  client: "Client",
  former_client: "Former Client",
  suspended: "Suspended",
};

/** UI select options — values come from `COMPANY_STATUS_VALUES` (single source of truth). */
export const COMPANY_STATUS_OPTIONS = COMPANY_STATUS_VALUES.map((value) => ({
  label: COMPANY_STATUS_LABELS[value],
  value,
}));

export const COMPANY_QUICK_FILTERS: QuickFilter[] = [
  {
    id: "name",
    label: "Name",
    type: "text" as const,
    field: "name",
    placeholder: "Search by name...",
  },
  {
    id: "status",
    label: "Status",
    type: "select" as const,
    field: "status",
    placeholder: "Select status...",
    options: COMPANY_STATUS_OPTIONS,
  },
  {
    id: "has_active_deals",
    label: "Has Deals",
    type: "boolean" as const,
    field: "has_active_deals",
    placeholder: "Has active deals...",
  },
  {
    id: "industry",
    label: "Industry",
    type: "select" as const,
    field: "industry",
    placeholder: "Select industry...",
    options: INDUSTRY_OPTIONS,
  },
];

export const CREATE_COMPANY_FIELD_ORDER: string[] = [
  "name",
  "status",
  "industry",
  "website",
  "address",
  "city",
  "state",
  "zip_code",
  "country",
  "contract_type",
  "hourly_rate",
  "monthly_rate",
  "payment_terms",
  "tax_id",
  "description",
  "notes",
  "domain",
  "organization_revenue",
  "current_technologies",
  "social_links",
  "employee_size",
  "tags",
];

/**
 * Progressive-disclosure section layout for the Create Company form.
 * See plan `the-log-activity-modal-enumerated-whisper.md`.
 *
 * Typed as a factory returning the canonical EntityFormSectionConfig array
 * — `SimpleTableConfig.createFormSections` expects a `() => readonly EntityFormSectionConfig[]`.
 */
export const CREATE_COMPANY_FORM_SECTIONS = () =>
  [
    {
      id: "core",
      kind: "always",
      fieldNames: ["name", "status"],
    },
    {
      id: "classification",
      kind: "collapsible",
      summary: "Industry, employee size, tags",
      fieldNames: ["industry", "employee_size", "tags"],
    },
    {
      id: "location",
      kind: "collapsible",
      fieldNames: ["address", "city", "state", "zip_code", "country"],
    },
    {
      id: "contact-channels",
      kind: "collapsible",
      summary: "Website, domain, social links",
      fieldNames: ["website", "domain", "social_links"],
    },
    {
      id: "commercial",
      kind: "collapsible",
      title: "Commercial",
      summary: "Contract, rates, payment terms, tax ID, revenue",
      fieldNames: [
        "contract_type",
        "hourly_rate",
        "monthly_rate",
        "payment_terms",
        "tax_id",
        "organization_revenue",
      ],
    },
    {
      id: "description",
      kind: "collapsible",
      title: "Notes & technologies",
      fieldNames: ["description", "notes", "current_technologies"],
    },
  ] as const;
